import { marketplaceService, MarketplaceHardware, MarketplaceSoftware } from '../../services/marketplaceService';
import {
  EnrichmentDetail,
  ProductEnrichment,
} from '../../services/distributorService';

/**
 * Bridge: take an approved enrichment + a chosen domain and publish it as
 * a Product into the partner's All-Products snapshot. Mirrors what the
 * "Add Product" → save flow in PartnerMarketplaceView does, but scoped to
 * the single-product case so it can be invoked from the Enrichment
 * review surface without prop-drilling through 5 levels.
 *
 * Side-effects:
 *  - Reads the current All-Products snapshot (or creates one if missing).
 *  - Appends a MarketplaceHardware / MarketplaceSoftware entry built from
 *    the enrichment + live price/stock.
 *  - Writes the updated snapshot back via createPartnerSnapshotBlob.
 *  - Returns the new product id (UUID generated client-side).
 *
 * Idempotency:
 *  - If a product with the same SKU + source='ingram' already lives in
 *    the snapshot, the existing entry is updated in place rather than
 *    duplicated. The next sync's price/stock refresh will continue to
 *    update that entry.
 */

export type PublishDomain =
  | 'computers'
  | 'networking'
  | 'mobile-hardware'
  | 'unified-collaboration'
  | 'software';

export interface PublishOptions {
  /** Distributor source key — currently always 'ingram' from this surface. */
  source: 'ingram';
  /** Distributor partner-facing label, e.g. 'Ingram Micro'. */
  sourceLabel: string;
  /** Distributor SKU (Ingram part number). */
  sku: string;
  /** Partner-confirmed domain mapping. */
  domain: PublishDomain;
  /** Optional manual sell-price override; defaults to distributor price plus the default markup. */
  sellPriceOverride?: number;
  /** Markup % applied to derive sell price from buy. Persisted on the row for future syncs. */
  markupPercent?: number;
  /** Image URL override (otherwise enrichment.imageUrl). */
  imageUrlOverride?: string | null;
  /** Optional canonical-name override (otherwise enrichment.canonicalName). */
  nameOverride?: string;
  /** Optional sub-category override (otherwise enrichment.category.secondary). */
  subCategoryOverride?: string | null;
}

/** Map an enrichment category L1 string to a sensible default Product domain. */
export function suggestDomain(category: string | null | undefined): PublishDomain {
  if (!category) return 'computers';
  const c = category.toLowerCase();
  if (c.includes('software'))  return 'software';
  if (c.includes('network'))   return 'networking';
  if (c.includes('security'))  return 'networking'; // network-adjacent
  if (c.includes('mobile') || c.includes('phone') || c.includes('tablet')) return 'computers';
  if (c.includes('audio') || c.includes('video') || c.includes('av') || c.includes('confer')) return 'unified-collaboration';
  return 'computers';
}

export function suggestCatalogueProductName(
  detail: EnrichmentDetail,
  enrichment: ProductEnrichment,
  fallbackSku: string = detail.sku,
): string {
  const brand = resolveCatalogueBrand(detail, enrichment, '');
  const candidates = [
    detail.source?.displayDescription,
    detail.source?.shortDescription,
    enrichment.canonicalName,
  ];

  for (const candidate of candidates) {
    const hpModelName = extractHpCommercialModelName(candidate, brand);
    if (hpModelName) return hpModelName;
  }

  const canonical = stripBrandPrefix(enrichment.canonicalName, brand);
  const cleanedCanonical = trimDescriptionTail(canonical);
  if (cleanedCanonical) return cleanedCanonical;

  for (const candidate of candidates) {
    const cleanedCandidate = trimDescriptionTail(stripBrandPrefix(candidate, brand));
    if (cleanedCandidate) return cleanedCandidate;
  }

  return fallbackSku;
}

function resolveCatalogueBrand(
  detail: EnrichmentDetail,
  enrichment: ProductEnrichment,
  fallback: string,
): string {
  return normaliseBrand(detail.source?.vendor)
    ?? normaliseBrand(extractVendor(enrichment.canonicalName))
    ?? fallback;
}

function normaliseBrand(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const aliases: Record<string, string> = {
    HEWLETT: 'HP',
    'HEWLETT PACKARD': 'HP',
    HEWLETT_PACKARD: 'HP',
    HP: 'HP',
    HPI: 'HP',
    'HP INC': 'HP',
    'HP INC.': 'HP',
    LENOVO: 'Lenovo',
    DELL: 'Dell',
    CISCO: 'Cisco',
    FORTINET: 'Fortinet',
    MICROSOFT: 'Microsoft',
    APPLE: 'Apple',
    ARUBA: 'Aruba',
    LOGITECH: 'Logitech',
    APC: 'APC',
  };
  const key = trimmed.replace(/\s+/g, ' ').toUpperCase();
  return aliases[key] ?? trimmed;
}

function cleanText(value: string | null | undefined): string {
  return (value ?? '')
    .replace(/[“”]/g, '"')
    .replace(/[™®©]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripBrandPrefix(value: string | null | undefined, brand: string | null | undefined): string {
  let text = cleanText(value);
  if (!text) return '';
  const candidates = [
    brand,
    brand === 'HP' ? 'Hewlett Packard' : null,
    brand === 'HP' ? 'HPI' : null,
  ]
    .filter((v): v is string => !!v)
    .map((v) => escapeRegex(v));

  if (candidates.length > 0) {
    text = text.replace(new RegExp(`^(?:${candidates.join('|')})\\b[\\s:-]*`, 'i'), '').trim();
  }
  return text;
}

function trimDescriptionTail(value: string | null | undefined): string {
  let text = cleanText(value)
    .replace(/\s+\|\s+.*$/, '')
    .replace(/\s+[–—]\s+.*$/, '')
    .replace(/\s+-\s+.*$/, '');
  if (!text) return '';

  const sizeMatch = text.match(/^(.+?\b\d{2})\s*(?:inch|in\.?|")\b/i);
  if (sizeMatch?.[1]) return cleanText(sizeMatch[1]);

  text = text.replace(/\b(?:Notebook|Laptop|Desktop|Workstation|Tablet|PC)\b.*$/i, '').trim();
  return cleanText(text);
}

function extractHpCommercialModelName(value: string | null | undefined, brand: string | null | undefined): string | null {
  const raw = cleanText(value);
  if (!raw) return null;
  const looksHp = normaliseBrand(brand) === 'HP' || /^HP\b/i.test(raw) || /^Hewlett\s+Packard\b/i.test(raw);
  if (!looksHp && !/\b(?:Pro\s*Book|ProBook|EliteBook|ZBook)\b/i.test(raw)) return null;

  const withoutBrand = stripBrandPrefix(raw, 'HP');
  const tokens = withoutBrand
    .replace(/[(),]/g, ' ')
    .split(/\s+/)
    .map((token) => token.replace(/^[^\w]+|[^\w."-]+$/g, ''))
    .filter(Boolean);

  for (let i = 0; i < tokens.length; i++) {
    const family = normaliseHpFamily(tokens, i);
    if (!family) continue;

    const output: string[] = [family.label];
    let cursor = family.nextIndex;
    while (cursor < tokens.length && output.length < 7) {
      const token = tokens[cursor];
      const lower = token.toLowerCase();
      if (HP_MODEL_STOP_WORDS.has(lower)) break;
      if (isCpuOrSpecStart(token)) break;

      if (isDisplaySizeToken(token)) {
        output.push(normaliseDisplaySize(token));
        break;
      }

      output.push(normaliseModelToken(token));
      cursor++;
    }

    if (output.length >= 2) {
      return output.join(' ');
    }
  }

  return null;
}

const HP_MODEL_STOP_WORDS = new Set([
  'notebook',
  'laptop',
  'desktop',
  'workstation',
  'tablet',
  'pc',
  'business',
  'wolf',
  'security',
  'edition',
  'with',
  'for',
]);

function normaliseHpFamily(tokens: string[], index: number): { label: string; nextIndex: number } | null {
  const token = tokens[index].toLowerCase();
  if (token === 'pb') return { label: 'ProBook', nextIndex: index + 1 };
  if (token === 'probook' || token === 'pro-book') return { label: 'ProBook', nextIndex: index + 1 };
  if (token === 'pro' && tokens[index + 1]?.toLowerCase() === 'book') return { label: 'ProBook', nextIndex: index + 2 };
  if (token === 'elitebook' || token === 'elite-book') return { label: 'EliteBook', nextIndex: index + 1 };
  if (token === 'zbook' || token === 'z-book') return { label: 'ZBook', nextIndex: index + 1 };
  return null;
}

function isDisplaySizeToken(token: string): boolean {
  return /^(?:1[0-9]|2[0-4])(?:(?:\.0)?(?:in|inch|"))?$/i.test(token);
}

function normaliseDisplaySize(token: string): string {
  const match = token.match(/\d{2}/);
  return match?.[0] ?? token;
}

function normaliseModelToken(token: string): string {
  const cleaned = token.replace(/["]/g, '');
  const generation = cleaned.match(/^g(\d+)([a-z])?$/i);
  if (generation) return `G${generation[1]}${generation[2]?.toLowerCase() ?? ''}`;
  return cleaned;
}

function isCpuOrSpecStart(token: string): boolean {
  return /^(?:Intel|Core|Ultra|Ryzen|AMD|Snapdragon|i[3579]|GB|TB|SSD|DDR|Win(?:dows)?)$/i.test(token);
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const ALL_PRODUCTS_NAME = 'All Products';
const DEFAULT_DISTRIBUTOR_MARKUP_PERCENT = 18;

async function resolveAllProductsSnapshotId(): Promise<string> {
  const list = await marketplaceService.listPartnerSnapshotsBlob();
  const existing = list.find((s) => s.name === ALL_PRODUCTS_NAME);
  if (existing) return existing.snapshotId;
  // Create one — this matches the PartnerMarketplaceView bootstrap path.
  await marketplaceService.createPartnerSnapshotBlob({
    name: ALL_PRODUCTS_NAME,
    notes: 'Default catalogue containing all your products',
    hardware: [],
    software: [],
  });
  const list2 = await marketplaceService.listPartnerSnapshotsBlob();
  const created = list2.find((s) => s.name === ALL_PRODUCTS_NAME);
  if (!created) throw new Error('Failed to bootstrap All Products snapshot');
  return created.snapshotId;
}

function newId(): string {
  // Lightweight UUID v4-ish — matches what marketplaceService accepts.
  return 'p-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
}

interface PublishResult {
  productId: string;
  updated: boolean; // false = newly created, true = updated existing entry
  arrayKind: 'hardware' | 'software';
}

export async function publishEnrichmentToCatalogue(
  detail: EnrichmentDetail,
  enrichment: ProductEnrichment,
  options: PublishOptions,
): Promise<PublishResult> {
  const snapshotId = await resolveAllProductsSnapshotId();
  const snapshot = await marketplaceService.getPartnerSnapshotBlob(snapshotId);

  const livePrice = detail.priceAndStock?.price ?? 0;
  const liveStock = detail.priceAndStock?.stock ?? 0;
  const markupPercent = options.markupPercent ?? DEFAULT_DISTRIBUTOR_MARKUP_PERCENT;
  const sellPrice = options.sellPriceOverride
    ?? Math.round(livePrice * (1 + markupPercent / 100) * 100) / 100;

  const resolvedImage = options.imageUrlOverride !== undefined
    ? options.imageUrlOverride ?? undefined
    : enrichment.imageUrl ?? undefined;
  const resolvedName = options.nameOverride?.trim()
    || suggestCatalogueProductName(detail, enrichment, options.sku);
  const resolvedSubCategory = options.subCategoryOverride !== undefined
    ? options.subCategoryOverride ?? undefined
    : enrichment.category?.secondary ?? undefined;
  const resolvedBrand = resolveCatalogueBrand(detail, enrichment, options.sourceLabel);
  const vendorPartNumber = detail.source?.vendorPartNumber?.trim() || undefined;

  const targetKind: 'hardware' | 'software' = options.domain === 'software' ? 'software' : 'hardware';
  const sharedFields = {
    name: resolvedName,
    brand: resolvedBrand,
    category: options.domain === 'software' ? 'General' : options.domain,
    description: enrichment.marketingSummary ?? '',
    image: resolvedImage,
    domain: options.domain,
    source: options.source,
    sku: options.sku,
    vendorPartNumber,
    soh: liveStock,
    buyPrice: livePrice,
    markupPercent,
    rrpExTax: detail.priceAndStock?.rrp ?? undefined,
    enabled: true,
    subCategory: resolvedSubCategory,
  };

  if (targetKind === 'hardware') {
    const hardware = (snapshot.hardware || []) as MarketplaceHardware[];
    const existingIdx = hardware.findIndex(
      (h) => (h as any).sku === options.sku && (h as any).source === options.source,
    );
    const updatedEntry: MarketplaceHardware = {
      ...(existingIdx >= 0 ? hardware[existingIdx] : ({} as MarketplaceHardware)),
      id: existingIdx >= 0 ? hardware[existingIdx].id : newId(),
      ...sharedFields,
      price: sellPrice,
      pricingType: 'lumpSum', // computers / networking / unified-collab are lumpSum
      term: 0,
      billingCycle: 'monthly',
      specs: existingIdx >= 0 ? (hardware[existingIdx].specs ?? []) : [],
    } as MarketplaceHardware;
    const nextHardware =
      existingIdx >= 0
        ? hardware.map((h, i) => (i === existingIdx ? updatedEntry : h))
        : [...hardware, updatedEntry];
    await marketplaceService.createPartnerSnapshotBlob({
      snapshotId,
      name: snapshot.name,
      notes: (snapshot as any).notes || '',
      hardware: nextHardware,
      software: snapshot.software || [],
      consulting: (snapshot as any).consulting || [],
    });
    return {
      productId: updatedEntry.id,
      updated: existingIdx >= 0,
      arrayKind: 'hardware',
    };
  }

  // software path — snapshot accepts distributor-specific fields even though
  // the strict type doesn't expose them. Cast to any for those extras.
  const software = (snapshot.software || []) as MarketplaceSoftware[];
  const existingIdx = software.findIndex(
    (s) => (s as any).sku === options.sku && (s as any).source === options.source,
  );
  const updatedEntry: MarketplaceSoftware = {
    ...(existingIdx >= 0 ? software[existingIdx] : ({} as MarketplaceSoftware)),
    id: existingIdx >= 0 ? software[existingIdx].id : newId(),
    name: sharedFields.name,
    vendor: resolvedBrand,
    category: sharedFields.category,
    price: sellPrice,
    pricingType: 'lumpSum',
    term: 0,
    billingCycle: 'monthly',
    description: sharedFields.description,
    image: sharedFields.image,
    enabled: true,
    subCategory: sharedFields.subCategory,
    // Distributor-specific fields surface as untyped extras on the blob.
    ...({
      domain: sharedFields.domain,
      source: sharedFields.source,
      sku: sharedFields.sku,
      vendorPartNumber: sharedFields.vendorPartNumber,
      soh: sharedFields.soh,
      buyPrice: sharedFields.buyPrice,
      markupPercent: sharedFields.markupPercent,
      rrpExTax: sharedFields.rrpExTax,
      brand: sharedFields.brand,
    } as any),
  };
  const nextSoftware =
    existingIdx >= 0
      ? software.map((s, i) => (i === existingIdx ? updatedEntry : s))
      : [...software, updatedEntry];
  await marketplaceService.createPartnerSnapshotBlob({
    snapshotId,
    name: snapshot.name,
    notes: (snapshot as any).notes || '',
    hardware: snapshot.hardware || [],
    software: nextSoftware,
    consulting: (snapshot as any).consulting || [],
  });
  return {
    productId: updatedEntry.id,
    updated: existingIdx >= 0,
    arrayKind: 'software',
  };
}

function extractVendor(canonicalName: string | null | undefined): string | null {
  if (!canonicalName) return null;
  const first = canonicalName.trim().split(/\s+/)[0];
  if (!first) return null;
  const aliases: Record<string, string> = {
    LENOVO: 'Lenovo', HP: 'HP', DELL: 'Dell', CISCO: 'Cisco',
    FORTINET: 'Fortinet', MICROSOFT: 'Microsoft', APPLE: 'Apple',
    ARUBA: 'Aruba', LOGITECH: 'Logitech', APC: 'APC',
  };
  return aliases[first.toUpperCase()] ?? first;
}
